import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ProviderSpecialityItem } from '@/types/settings-api';

/**
 * Canonical React Query key for a practitioner's speciality list.
 */
export function providerSpecialityQueryKey(
  doctorId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'provider-speciality', doctorId];
}

/**
 * Specialities assigned to a practitioner — PROD-1826 (appointment-profile
 * Specialty dropdown).
 *
 *   GET /api/Doctor/ProviderSpeciality/{doctorId}
 *
 * Returns the SNOMED specialities the provider is credentialed for
 * (verified against dev cURL 2026-08-06). The appointment-profile editor
 * uses each item's `speciality.code.displayName` as the option label and
 * writes `specialityId` (+ the full `speciality` object) into the profile.
 *
 * Flutter source: `settings_data_source_impl.dart:1521` (`fetchProviderSpeciality`).
 */
export function useProviderSpeciality(doctorId: string | null | undefined) {
  return useQuery({
    queryKey: providerSpecialityQueryKey(doctorId),
    queryFn: ({ signal }) => {
      if (!doctorId) {
        throw new Error('useProviderSpeciality called without doctorId');
      }
      return get<ProviderSpecialityItem[]>(
        `/api/Doctor/ProviderSpeciality/${doctorId}`,
        { signal },
      );
    },
    enabled: !!doctorId,
    staleTime: 60_000,
  });
}
